/**
 * Feature Definitions & Comparison Data
 *
 * Defines features for plan comparison tables and feature lists.
 *
 * @module @hummingbird/pricing/features
 */

import type { PlanId, ComparisonFeature, FeatureCategory, BonusAction } from './types.js';

// =============================================================================
// Feature Category Labels
// =============================================================================

export const FEATURE_CATEGORY_LABELS: Record<FeatureCategory, string> = {
  requests: 'Usage & limits',
  performance: 'Performance',
  api_access: 'What you can call',
  team: 'Work with your team',
  support: 'When you need help',
  compliance: 'Trust and compliance',
};

// =============================================================================
// Comparison Features
// =============================================================================

/**
 * All features for plan comparison table
 * Order determines display order within category
 */
export const COMPARISON_FEATURES: ComparisonFeature[] = [
  // Usage & Limits
  {
    id: 'monthly_requests',
    name: 'Monthly Requests',
    tooltip: 'Maximum API requests per billing period. Resets monthly for paid plans.',
    category: 'requests',
    values: {
      free: '1K-2K',
      starter: '15K-50K',
      pro: '60K-250K',
      elite: '275K-2M',
      enterprise: '2M+',
    },
  },
  {
    id: 'api_keys',
    name: 'API Keys',
    tooltip: 'Number of unique API keys you can create for different applications or environments.',
    category: 'requests',
    values: {
      free: 1,
      starter: 5,
      pro: 20,
      elite: 50,
      enterprise: 'Unlimited',
    },
  },

  // Performance
  {
    id: 'rate_limit',
    name: 'Rate Limit',
    tooltip: 'Maximum requests per second. Higher limits enable burst traffic handling.',
    category: 'performance',
    values: {
      free: '5/sec',
      starter: '25/sec',
      pro: '50/sec',
      elite: '250/sec',
      enterprise: 'Custom',
    },
  },
  {
    id: 'global_edge',
    name: 'Global performance',
    tooltip: 'Multi-region deployment with intelligent routing. Sub-200ms p95 latency worldwide.',
    category: 'performance',
    values: {
      free: true,
      starter: true,
      pro: true,
      elite: true,
      enterprise: true,
    },
  },

  // API Access
  {
    id: 'core_apis',
    name: 'Core APIs',
    tooltip: 'Access to standard API endpoints including geolocation, IP lookup, and more.',
    category: 'api_access',
    values: {
      free: true,
      starter: true,
      pro: true,
      elite: true,
      enterprise: true,
    },
  },
  {
    id: 'premium_apis',
    name: 'Premium APIs',
    tooltip: 'Access to advanced API endpoints with enriched data and additional fields.',
    category: 'api_access',
    values: {
      free: false,
      starter: false,
      pro: true,
      elite: true,
      enterprise: true,
    },
  },
  {
    id: 'bulk_endpoints',
    name: 'Bulk Endpoints',
    tooltip: 'Process multiple requests in a single API call for batch operations.',
    category: 'api_access',
    values: {
      free: false,
      starter: false,
      pro: true,
      elite: true,
      enterprise: true,
    },
  },

  // Team & Collaboration
  {
    id: 'team_members',
    name: 'Team Members',
    tooltip: 'Add team members to your organization. Available as an add-on for Pro+ plans.',
    category: 'team',
    values: {
      free: '1 (owner)',
      starter: '1 (owner)',
      pro: 'Add-on ($1.99/user)',
      elite: 'Add-on ($1.99/user)',
      enterprise: 'Custom',
    },
  },
  {
    id: 'roles_permissions',
    name: 'Roles & Permissions',
    tooltip: 'Assign different access levels to team members (admin, developer, viewer).',
    category: 'team',
    values: {
      free: false,
      starter: false,
      pro: true,
      elite: true,
      enterprise: true,
    },
  },

  // Support
  {
    id: 'support_level',
    name: 'Support',
    tooltip: 'Level of support included with your plan. Can be upgraded via add-on.',
    category: 'support',
    values: {
      free: 'Community',
      starter: 'Email (48h)',
      pro: 'Email (24h)',
      elite: 'Priority (4h)',
      enterprise: 'Dedicated',
    },
  },
  {
    id: 'documentation',
    name: 'Docs & Guides',
    tooltip: 'Access to comprehensive API documentation, tutorials, and integration guides.',
    category: 'support',
    values: {
      free: true,
      starter: true,
      pro: true,
      elite: true,
      enterprise: true,
    },
  },

  // Security & Compliance
  {
    id: 'sla',
    name: 'SLA Guarantee',
    tooltip: 'Service Level Agreement with guaranteed uptime and credits for downtime.',
    category: 'compliance',
    values: {
      free: false,
      starter: false,
      pro: false,
      elite: '99.9%',
      enterprise: '99.99%',
    },
  },
  {
    id: 'sso',
    name: 'SSO / SAML',
    tooltip: 'Single Sign-On integration with your identity provider (Okta, Azure AD, etc.).',
    category: 'compliance',
    values: {
      free: false,
      starter: false,
      pro: false,
      elite: false,
      enterprise: true,
    },
  },
  {
    id: 'custom_domains',
    name: 'Custom Domains',
    tooltip: 'Use your own domain for API endpoints. Available as an add-on.',
    category: 'compliance',
    values: {
      free: false,
      starter: false,
      pro: 'Add-on',
      elite: 'Add-on',
      enterprise: 'Included',
    },
  },
];

// =============================================================================
// Feature Helpers
// =============================================================================

/**
 * Get features grouped by category
 */
export function getFeaturesByCategory(): Record<FeatureCategory, ComparisonFeature[]> {
  const grouped: Record<FeatureCategory, ComparisonFeature[]> = {
    requests: [],
    performance: [],
    api_access: [],
    team: [],
    support: [],
    compliance: [],
  };

  for (const feature of COMPARISON_FEATURES) {
    grouped[feature.category].push(feature);
  }

  return grouped;
}

/**
 * Get ordered categories for display
 */
export function getOrderedCategories(): FeatureCategory[] {
  return ['requests', 'performance', 'api_access', 'team', 'support', 'compliance'];
}

/**
 * Get feature value display for a specific plan
 */
export function getFeatureValueDisplay(feature: ComparisonFeature, planId: PlanId): string {
  const value = feature.values[planId];

  if (typeof value === 'boolean') {
    return value ? '✓' : '—';
  }

  if (typeof value === 'number') {
    if (value === -1) return 'Unlimited';
    return value.toLocaleString();
  }

  return value;
}

// =============================================================================
// Benefit Buckets (Short Feature Lists for Plan Cards)
// =============================================================================

export interface BenefitBucket {
  planId: PlanId;
  benefits: string[];
}

/**
 * Short benefit lists for plan cards (legacy - use PLAN_BENEFIT_BUCKETS instead)
 */
export const PLAN_BENEFITS: BenefitBucket[] = [
  {
    planId: 'free',
    benefits: [
      '1,000 requests',
      '+1,000 bonus via quick actions',
      '1 API key',
      'Community support',
    ],
  },
  {
    planId: 'starter',
    benefits: [
      'Up to 50K reqs/mo',
      '5 API keys',
      '25 req/sec rate limit',
      'Email support (48h)',
    ],
  },
  {
    planId: 'pro',
    benefits: [
      'Up to 250K reqs/mo',
      '20 API keys',
      'Premium APIs & bulk endpoints',
      'Team members available',
      'Email support (24h)',
    ],
  },
  {
    planId: 'elite',
    benefits: [
      'Up to 2M reqs/mo',
      '50 API keys',
      '250 req/sec rate limit',
      '99.9% SLA guarantee',
      'Priority support (4h)',
    ],
  },
  {
    planId: 'enterprise',
    benefits: [
      'Unlimited requests',
      'Unlimited API keys',
      'Custom rate limits',
      '99.99% SLA guarantee',
      'SSO/SAML included',
      'Dedicated support',
    ],
  },
];

/**
 * Get benefits for a specific plan (legacy)
 */
export function getPlanBenefits(planId: PlanId): string[] {
  return PLAN_BENEFITS.find((b) => b.planId === planId)?.benefits ?? [];
}

// =============================================================================
// Categorized Benefit Buckets (New - with tooltips and categories)
// =============================================================================

/**
 * A single benefit item with tooltip
 */
export interface BenefitItem {
  /** Display text */
  label: string;
  /** Tooltip description on hover */
  tooltip: string;
  /** Optional badge (e.g., "NEW", "POPULAR") */
  badge?: string;
}

/**
 * A category of benefits within a plan
 */
export interface BenefitCategory {
  /** Category header (e.g., "Key Features", "API Access") */
  header: string;
  /** Benefits in this category */
  items: BenefitItem[];
}

/**
 * Complete benefit structure for a plan
 */
export interface PlanBenefitBuckets {
  planId: PlanId;
  /** Intro line (e.g., "Everything in Starter, plus:") */
  inheritFrom?: string;
  /** Categorized benefits */
  categories: BenefitCategory[];
}

/**
 * Categorized benefits for each plan with tooltips
 */
export const PLAN_BENEFIT_BUCKETS: PlanBenefitBuckets[] = [
  {
    planId: 'free',
    categories: [
      {
        header: 'Get Started Free',
        items: [
          {
            label: '2,000 lifetime requests',
            tooltip: 'Use them on any API',
          },
          {
            label: '1 API key',
            tooltip: 'One key to start integrating',
          },
          {
            label: '5 requests/sec',
            tooltip: 'Rate limit for free tier',
          },
          {
            label: 'Every API endpoint included',
            tooltip: 'Full access to all API endpoints',
          },
          {
            label: 'Documentation and guides',
            tooltip: 'Comprehensive docs with code examples',
          },
        ],
      },
    ],
  },
  {
    planId: 'starter',
    inheritFrom: 'Everything in Free, plus:',
    categories: [
      {
        header: 'Usage & Limits',
        items: [
          {
            label: 'Up to 50K reqs/mo',
            tooltip: 'Resets each billing period',
          },
          {
            label: '5 API keys',
            tooltip: 'For different apps or environments',
          },
          {
            label: '25 reqs/sec',
            tooltip: 'Higher rate limit for production',
          },
        ],
      },
      {
        header: 'Support',
        items: [
          {
            label: 'Email support (48h)',
            tooltip: '48-hour response time',
          },
        ],
      },
    ],
  },
  {
    planId: 'pro',
    inheritFrom: 'Everything in Starter, plus:',
    categories: [
      {
        header: 'Usage & Limits',
        items: [
          {
            label: 'Up to 250K reqs/mo',
            tooltip: 'For growing applications',
          },
          {
            label: '20 API keys',
            tooltip: 'For complex deployments',
          },
          {
            label: '50 reqs/sec',
            tooltip: 'Handle burst traffic',
          },
        ],
      },
      {
        header: 'Advanced Features',
        items: [
          {
            label: 'Team members',
            tooltip: '$1.99/user/month add-on',
            badge: 'POPULAR',
          },
        ],
      },
      {
        header: 'Support',
        items: [
          {
            label: 'Email support (24h)',
            tooltip: '24-hour response time',
          },
        ],
      },
    ],
  },
  {
    planId: 'elite',
    inheritFrom: 'Everything in Pro, plus:',
    categories: [
      {
        header: 'Usage & Limits',
        items: [
          {
            label: 'Up to 2M reqs/mo',
            tooltip: 'For high-volume production',
          },
          {
            label: '50 API keys',
            tooltip: 'Enterprise-grade key management',
          },
          {
            label: '250 reqs/sec',
            tooltip: 'For high-traffic applications',
          },
        ],
      },
      {
        header: 'Enterprise Features',
        items: [
          {
            label: '99.9% SLA guarantee',
            tooltip: 'Credits for any downtime',
          },
          {
            label: 'Custom domains',
            tooltip: 'Add-on available',
          },
        ],
      },
      {
        header: 'Support',
        items: [
          {
            label: 'Priority support (4h)',
            tooltip: '4-hour response time',
          },
        ],
      },
    ],
  },
  {
    planId: 'enterprise',
    inheritFrom: 'Everything in Elite, plus:',
    categories: [
      {
        header: 'Unlimited Scale',
        items: [
          {
            label: 'Unlimited requests',
            tooltip: 'No monthly caps',
          },
          {
            label: 'Unlimited API keys',
            tooltip: 'No key limits',
          },
          {
            label: 'Custom rate limits',
            tooltip: 'Tailored to your needs',
          },
        ],
      },
      {
        header: 'Enterprise Security',
        items: [
          {
            label: 'SSO/SAML',
            tooltip: 'Okta, Azure AD, and more',
            badge: 'EXCLUSIVE',
          },
          {
            label: '99.99% SLA guarantee',
            tooltip: 'Premium uptime guarantee',
          },
          {
            label: 'Custom domains included',
            tooltip: 'White-label endpoints',
          },
        ],
      },
      {
        header: 'Premium Support',
        items: [
          {
            label: 'Dedicated support',
            tooltip: 'Named contact for your org',
          },
        ],
      },
    ],
  },
];

/**
 * Get categorized benefits for a specific plan
 */
export function getPlanBenefitBuckets(planId: PlanId): PlanBenefitBuckets | null {
  return PLAN_BENEFIT_BUCKETS.find((b) => b.planId === planId) || null;
}

// =============================================================================
// Free Tier Bonus Actions
// =============================================================================

export const FREE_TIER_BASE_REQUESTS = 1000;
export const FREE_TIER_MAX_BONUS = 1000;
export const FREE_TIER_MAX_TOTAL = FREE_TIER_BASE_REQUESTS + FREE_TIER_MAX_BONUS;

export const BONUS_ACTIONS: BonusAction[] = [
  {
    id: 'verify_phone',
    name: 'Verify Phone',
    description: 'Add and verify your phone number',
    bonusRequests: 400,
    icon: 'Phone',
  },
  {
    id: 'add_payment_method',
    name: 'Add Payment Method',
    description: 'Add a credit card for future upgrades',
    bonusRequests: 300,
    icon: 'CreditCard',
  },
  {
    id: 'complete_profile',
    name: 'Complete Profile',
    description: 'Fill out your company and use case',
    bonusRequests: 100,
    icon: 'User',
  },
  {
    id: 'create_api_key',
    name: 'Create API Key',
    description: 'Generate your first API key',
    bonusRequests: 100,
    icon: 'Key',
  },
  {
    id: 'first_api_call',
    name: 'Make First Call',
    description: 'Successfully call an API endpoint',
    bonusRequests: 100,
    icon: 'Zap',
  },
];

/**
 * Calculate total free tier requests including bonuses
 */
export function calculateFreeTierTotal(completedBonusIds: string[]): number {
  let total = FREE_TIER_BASE_REQUESTS;

  for (const action of BONUS_ACTIONS) {
    if (completedBonusIds.includes(action.id)) {
      total += action.bonusRequests;
    }
  }

  return Math.min(total, FREE_TIER_MAX_TOTAL);
}

/**
 * Get remaining bonus requests available
 */
export function getRemainingBonusRequests(completedBonusIds: string[]): number {
  const earned = BONUS_ACTIONS.filter((a) => completedBonusIds.includes(a.id)).reduce(
    (sum, a) => sum + a.bonusRequests,
    0
  );

  return FREE_TIER_MAX_BONUS - Math.min(earned, FREE_TIER_MAX_BONUS);
}
